/**
 * @module HypothesisWalkthrough
 * @purpose Stepped diagnostic walkthrough shell — progress dots, Back/Next buttons, arrow-key nav.
 * @depends react, react-i18next
 * @dependedOnBy HypothesisCard
 *
 * @rules
 * - Performance: pure display — no data fetching; step bodies are pre-rendered children
 * - State: activeIndex via useState (local only)
 * - Errors: n/a — purely presentational
 */

import { useState, useCallback, useId } from 'react'
import { useTranslation } from 'react-i18next'

export interface WalkthroughStep {
  title: string
  body: React.ReactElement
}

interface HypothesisWalkthroughProps {
  steps: WalkthroughStep[]
}

interface StepDotsProps {
  steps: WalkthroughStep[]
  activeIndex: number
  onSelect: (index: number) => void
  tabIdFor: (index: number) => string
  panelId: string
}

const STEPPER_STYLE: React.CSSProperties = {
  margin: '6px 0 0',
}

const DOTS_ROW: React.CSSProperties = {
  display: 'flex',
  gap: '6px',
  flexWrap: 'wrap',
  marginBottom: '8px',
}

const DOT_BASE: React.CSSProperties = {
  width: '28px',
  height: '28px',
  borderRadius: '50%',
  border: '1px solid var(--border)',
  background: 'var(--bg-card)',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  fontSize: '0.65rem',
  fontFamily: 'var(--font-mono)',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const DOT_ACTIVE: React.CSSProperties = {
  ...DOT_BASE,
  background: 'var(--accent)',
  color: 'var(--btn-primary-text, #fff)',
  border: '1px solid var(--accent)',
}

const STEP_LABEL: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.6rem',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '10px',
}

const PANEL_STYLE: React.CSSProperties = {
  minHeight: '80px',
}

const NAV_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: '16px',
  gap: '8px',
}

const BTN_NAV: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.65rem',
  padding: '4px 12px',
  borderRadius: '4px',
  border: '1px solid var(--border)',
  background: 'var(--bg-card)',
  color: 'var(--text)',
  cursor: 'pointer',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
}

const BTN_NAV_DISABLED: React.CSSProperties = {
  ...BTN_NAV,
  opacity: 0.4,
  cursor: 'not-allowed',
}

const PAGE_TITLE: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: '0.92rem',
  color: 'var(--text)',
  fontWeight: 600,
  marginBottom: '10px',
}

function StepDots({ steps, activeIndex, onSelect, tabIdFor, panelId }: StepDotsProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  return (
    <div role="tablist" style={DOTS_ROW}>
      {steps.map((step, index) => (
        <button
          key={step.title}
          type="button"
          role="tab"
          id={tabIdFor(index)}
          aria-controls={panelId}
          aria-selected={index === activeIndex}
          aria-label={t('reasoning.hypothesis.walkthrough.dotLabel', {
            step: index + 1,
            title: step.title,
          })}
          style={index === activeIndex ? DOT_ACTIVE : DOT_BASE}
          onClick={() => { onSelect(index) }}
        >
          {index + 1}
        </button>
      ))}
    </div>
  )
}

/**
 * Stepped walkthrough shell with keyboard navigation and ARIA tablist semantics.
 *
 * @behaviour Renders progress dots (role=tablist/tab with aria-controls), step label, active
 *   panel (role=tabpanel, aria-labelledby, aria-live=polite), and Back/Next buttons.
 *   Arrow-Left/Right navigate steps ONLY while focus is inside this walkthrough instance —
 *   multiple cards on one page step independently. Respects prefers-reduced-motion
 *   by omitting CSS transitions when the media query matches (no animation class applied).
 * @errors n/a
 */
export function HypothesisWalkthrough({ steps }: HypothesisWalkthroughProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const [activeIndex, setActiveIndex] = useState(0)
  const instanceId = useId()
  const panelId = `${instanceId}-panel`
  const tabIdFor = useCallback(
    (index: number) => `${instanceId}-tab-${String(index)}`,
    [instanceId],
  )

  const goTo = useCallback((index: number) => {
    const clamped = Math.max(0, Math.min(steps.length - 1, index))
    setActiveIndex(clamped)
  }, [steps.length])

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowRight') { goTo(activeIndex + 1) }
    if (event.key === 'ArrowLeft') { goTo(activeIndex - 1) }
  }, [activeIndex, goTo])

  const activeStep = steps[activeIndex] ?? steps[0]
  const total = steps.length
  const isFirst = activeIndex === 0
  const isLast = activeIndex === total - 1

  if (activeStep === undefined) {
    return <div />
  }

  return (
    <div style={STEPPER_STYLE} onKeyDown={handleKeyDown}>
      <StepDots
        steps={steps}
        activeIndex={activeIndex}
        onSelect={goTo}
        tabIdFor={tabIdFor}
        panelId={panelId}
      />
      <div style={STEP_LABEL}>
        {t('reasoning.hypothesis.walkthrough.stepOf', { current: activeIndex + 1, total })}
      </div>
      <div
        role="tabpanel"
        id={panelId}
        aria-labelledby={tabIdFor(activeIndex)}
        aria-live="polite"
        style={PANEL_STYLE}
      >
        <div style={PAGE_TITLE}>{activeStep.title}</div>
        {activeStep.body}
      </div>
      <div style={NAV_ROW}>
        <button
          type="button"
          style={isFirst ? BTN_NAV_DISABLED : BTN_NAV}
          disabled={isFirst}
          aria-disabled={isFirst}
          onClick={() => { goTo(activeIndex - 1) }}
        >
          {t('reasoning.hypothesis.walkthrough.back')}
        </button>
        <button
          type="button"
          style={isLast ? BTN_NAV_DISABLED : BTN_NAV}
          disabled={isLast}
          aria-disabled={isLast}
          onClick={() => { goTo(activeIndex + 1) }}
        >
          {t('reasoning.hypothesis.walkthrough.next')}
        </button>
      </div>
    </div>
  )
}
